import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import { filterPickerStreams } from "./filter-picker-streams";
import { type CappedPickerGroup, capPickerGroups, groupPickerStreams } from "./group-picker-streams";
import { type PickerRowText, pickerRowTexts } from "./picker-row-text";
import { GroupSection, ROW_CLASS, RowText, StatusLine } from "./picker-rows";
import type { PickerStream } from "./picker-stream";
import type { CatalogStatus } from "./use-picker-catalog";

const PlusIcon = iconRegistry[ICON_KEYS.PLUS];

/** Big orgs have thousands of streams; past this many, search instead of scrolling. */
export const PICKER_ROW_CAP = 100;

export function AvailableTab({
    catalog,
    plotted,
    status,
    onAdd,
    initialQuery = "",
}: {
    catalog: readonly PickerStream[];
    plotted: readonly PickerStream[];
    status: CatalogStatus;
    onAdd: (streamId: number) => void;
    initialQuery?: string;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { t: tMessages } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });
    const [query, setQuery] = useState(initialQuery);
    const thisZone = t("this-zone");
    const noZone = t("no-zone");

    const { groups, matchCount, truncated } = useMemo(() => {
        const plottedIds = new Set(plotted.map((stream) => stream.id));
        const matches = filterPickerStreams(catalog, plottedIds, query);
        return {
            groups: capPickerGroups(
                groupPickerStreams(matches.streams, { thisZone, noZone }, matches.rankById),
                PICKER_ROW_CAP,
            ),
            matchCount: matches.streams.length,
            truncated: matches.streams.length > PICKER_ROW_CAP,
        };
    }, [catalog, plotted, query, thisZone, noZone]);

    const searching = query.trim().length > 0;

    let body: React.ReactNode;
    if (status === "loading") {
        body = <StatusLine>{tMessages("loading")}</StatusLine>;
    } else if (status === "error") {
        body = <StatusLine>{tMessages("load-failed")}</StatusLine>;
    } else if (groups.length === 0) {
        body = <StatusLine>{searching ? t("no-matches", { query: query.trim() }) : t("none-left")}</StatusLine>;
    } else {
        body = groups.map((group) => <AvailableGroup key={group.key} group={group} onAdd={onAdd} />);
    }

    return (
        <>
            <div className={"p-2.5"}>
                <SearchInput
                    aria-label={t("search-label")}
                    clearable
                    endText={searching && matchCount > 0 ? t("match-count", { count: matchCount }) : undefined}
                    onValueChange={setQuery}
                    placeholder={t("search-placeholder")}
                    value={query}
                />
            </div>
            <div className={"min-h-0 flex-1 overflow-y-auto pb-1.5"}>{body}</div>
            {truncated && (
                <div className={"border-t bg-muted/40 px-3.5 py-2 text-muted-foreground text-xs"}>
                    {t("capped", { shown: PICKER_ROW_CAP, total: matchCount })}
                </div>
            )}
        </>
    );
}

function AvailableGroup({ group, onAdd }: { group: CappedPickerGroup; onAdd: (streamId: number) => void }) {
    const texts = pickerRowTexts(group.streams);
    return (
        <GroupSection label={group.label} count={group.total}>
            {group.streams.map((stream) => (
                <AvailableRow
                    key={stream.id}
                    stream={stream}
                    text={texts.get(stream.id) ?? { measurement: stream.name, detail: "" }}
                    onAdd={onAdd}
                />
            ))}
        </GroupSection>
    );
}

function AvailableRow({
    stream,
    text,
    onAdd,
}: {
    stream: PickerStream;
    text: PickerRowText;
    onAdd: (streamId: number) => void;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { measurement: name, detail } = text;
    return (
        <button
            type={"button"}
            aria-label={detail ? t("add-row", { name, detail }) : t("add-row-without-detail", { name })}
            className={`${ROW_CLASS} cursor-pointer hover:bg-accent`}
            onClick={() => onAdd(stream.id)}
        >
            <RowText stream={stream} text={text} />
            {/* Looks like a button, but the whole row is the button. */}
            <span aria-hidden className={buttonVariants({ size: "icon-xs", variant: "outline" })}>
                <PlusIcon className={"size-3"} />
            </span>
        </button>
    );
}
